import React, { useMemo } from "react";

import DashboardWidgetCard from "./components/DashboardWidgetCard";
import DashboardCardHeader from "./components/DashboardCardHeader";
import HeaderAndValue from "./components/HeaderAndValue";

import { makeStyles } from "@material-ui/core";
import ResponsiveBarChart from "./components/ResponsiveBarChart";

import useFetch from "./hooks/useFetch";
import Loading from "./components/Loading";
import { formatDate, formatToCommaString } from "./helper/format";
import ToggleButtonsTrasnferList from "./components/ToggleButton";

const monthName = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sept",
  "Oct",
  "Nov",
  "Dec",
];

const getPerc = (neu, deno) => {
  if (!deno) return "-";
  if (neu === 0) return "-";
  return `\n${((neu * 100.0) / deno).toFixed(2)}%`;
};

const withPreWrap = (label) => {
  if (label === "0") return "";
  return (
    <span style={{ fontSize: 14, margin: 0, whiteSpace: "pre-wrap" }}>
      ({label})
    </span>
  );
};

const warningDetermine = (neu, deno) => {
  const perc = (neu * 100.0) / deno;
  if (perc < 30.0) {
    return true;
  }

  return false;
};

const config = (row) => [
  {
    //label: "Month",
    value: monthName[row["month"] - 1] + "'" + (row["year"] % 100),
    valueEligible: monthName[row["month"] - 1] + "'" + (row["year"] % 100),
  },
  {
    //label: "Registration Count",
    value: formatToCommaString(row["registration_count"]),
    valueEligible: formatToCommaString(row["registration_count"]),
  },
  {
    //label: "M0",
    value: getPerc(row["M0_count"], row["registration_count"]),
    subValue: withPreWrap(formatToCommaString(row["M0_count"])),

    valueEligible: getPerc(row["M0_count_eligible"], row["registration_count"]),
    subValueEligible: withPreWrap(
      formatToCommaString(row["M0_count_eligible"])
    ),

    warnEligible: warningDetermine(
      row["M0_count_eligible"],
      row["registration_count"]
    ),

    warnOverall: warningDetermine(row["M0_count"], row["registration_count"]),
  },
  {
    //label: "M1",
    value: getPerc(row["M1_count"], row["registration_count"]),
    subValue: withPreWrap(formatToCommaString(row["M1_count"])),

    valueEligible: getPerc(row["M1_count_eligible"], row["registration_count"]),
    subValueEligible: withPreWrap(
      formatToCommaString(row["M1_count_eligible"])
    ),

    warnEligible: warningDetermine(
      row["M1_count_eligible"],
      row["registration_count"]
    ),

    warnOverall: warningDetermine(row["M1_count"], row["registration_count"]),
  },
  {
    //label: "M2",
    value: getPerc(row["M2_count"], row["registration_count"]),
    subValue: withPreWrap(formatToCommaString(row["M2_count"])),

    valueEligible: getPerc(row["M2_count_eligible"], row["registration_count"]),
    subValueEligible: withPreWrap(
      formatToCommaString(row["M2_count_eligible"])
    ),

    warnEligible: warningDetermine(
      row["M2_count_eligible"],
      row["registration_count"]
    ),
    warnOverall: warningDetermine(row["M2_count"], row["registration_count"]),
  },

  {
    //label: "M3",
    value: getPerc(row["M3_count"], row["registration_count"]),
    subValue: withPreWrap(formatToCommaString(row["M3_count"])),

    valueEligible: getPerc(row["M3_count_eligible"], row["registration_count"]),
    subValueEligible: withPreWrap(
      formatToCommaString(row["M3_count_eligible"])
    ),

    warnEligible: warningDetermine(
      row["M3_count_eligible"],
      row["registration_count"]
    ),
    warnOverall: warningDetermine(row["M3_count"], row["registration_count"]),
  },
  {
    //label: "M4",
    value: getPerc(row["M4_count"], row["registration_count"]),
    subValue: withPreWrap(formatToCommaString(row["M4_count"])),

    valueEligible: getPerc(row["M4_count_eligible"], row["registration_count"]),
    subValueEligible: withPreWrap(
      formatToCommaString(row["M4_count_eligible"])
    ),

    warnEligible: warningDetermine(
      row["M4_count_eligible"],
      row["registration_count"]
    ),
    warnOverall: warningDetermine(row["M4_count"], row["registration_count"]),
  },
  {
    //label: "M5",
    value: getPerc(row["M5_count"], row["registration_count"]),
    subValue: withPreWrap(formatToCommaString(row["M5_count"])),

    valueEligible: getPerc(row["M5_count_eligible"], row["registration_count"]),
    subValueEligible: withPreWrap(
      formatToCommaString(row["M5_count_eligible"])
    ),

    warnEligible: warningDetermine(
      row["M5_count_eligible"],
      row["registration_count"]
    ),
    warnOverall: warningDetermine(row["M5_count"], row["registration_count"]),
  },
];

const useStyle = makeStyles((theme) => {
  return {
    card: { margin: theme.spacing(2, 0) },
    cardHeader: {
      borderBottom: "1px solid rgba(0, 0, 0, 0.06)",
      paddingBottom: theme.spacing(1.5),
      marginTop: theme.spacing(0.5),
      display: "flex",
      flexDirection: "column",
      alignItems: "start",
    },
    updateDate: {
      color: "rgba(54, 43, 115, 0.56)",
      margin: "0",
      fontSize: 12,
    },
    bottomCardHeader: {
      borderTop: "1px solid rgba(0, 0, 0, 0.06)",
      paddingTop: theme.spacing(2),
      marginTop: theme.spacing(3),
    },
    container: {
      display: "flex",
      flexDirection: "column",
      width: "100%",
      justifyContent: "space-between",
    },
    widgetLineWrapper: {
      display: "flex",
      [theme.breakpoints.down("md")]: {
        flexWrap: "wrap",
        padding: "16px 0",
        borderBottom: "1px dashed rgba(199,199,199,0.56)",
      },
    },
    widget: {
      display: "flex",
      flexDirection: "column",
      background: "white",
      justifyContent: "center",
      alignItems: "center",
      borderRadius: theme.spacing(1.5),
      margin: theme.spacing(0.5),
      width: "calc(100%/8 - 8px)",

      [theme.breakpoints.down("md")]: {
        width: "calc(100%/4 - 8px)",
      },
      padding: theme.spacing(1),
    },
    chartContainer: {
      height: 400,
      marginTop: theme.spacing(2),
      overflow: "auto",
      position: "relative",
    },
    chartWrapper: {
      position: "absolute",
      left: 0,
      right: 0,
      bottom: 0,
      top: 0,
      height: 380,
      minWidth: 1000,
    },
  };
});

const headerRow = ["Month", "Registration", "M0", "M1", "M2", "M3", "M4", "M5"];

export default function AcquisitionOverview({ geoFilters }) {
  const params = useMemo(() => geoFilters, [geoFilters]);
  const classes = useStyle();
  const { data, isLoading } = useFetch(
    "/reports/dashboard/acquisition-overview",
    params
  );
  const { data: dayWise, isLoading: isDayWiseLoading } = useFetch(
    "/reports/dashboard/day-wise-acquisition",
    params
  );

  const [tabIndex, setTabIndex] = React.useState(0);
  const tabConfig = [{ label: "Eligible" }, { label: "Overall" }];
  const onTabChange = (index) => {
    if (index == null) return;
    setTabIndex(index);
  };

  return (
    <DashboardWidgetCard className={classes.card}>
      <DashboardCardHeader
        className={classes.cardHeader}
        label="Day wise Acquisition"
        subLabel={
          dayWise && (
            <p className={classes.updateDate}>
              Updated till - {formatDate(dayWise.last_parsed_time)}
            </p>
          )
        }
      ></DashboardCardHeader>

      {isDayWiseLoading ? (
        <Loading />
      ) : (
        dayWise && (
          <div className={classes.chartContainer}>
            <div className={classes.chartWrapper}>
              <ResponsiveBarChart
                data={dayWise.data
                  .sort((a, b) => {
                    const yearA = a.year
                      ? a.year
                      : a.month === 1
                      ? "2025"
                      : "2024";
                    const yearB = b.year
                      ? b.year
                      : b.month === 1
                      ? "2025"
                      : "2024";
                    const A = new Date(`${yearA}-${a.month}-${a.day}`);
                    const B = new Date(`${yearB}-${b.month}-${b.day}`);
                    if (A > B) {
                      return 1;
                    } else if (A < B) {
                      return -1;
                    }

                    return a.day - b.day;
                  })
                  .map((d) => ({
                    Day: d.day + " " + monthName[d.month - 1],
                    Acquisition: d.lead_count,
                  }))}
              />
            </div>
          </div>
        )
      )}
      <DashboardCardHeader
        className={classes.bottomCardHeader}
        label="Acquisition Overview"
        subLabel={
          data && (
            <p className={classes.updateDate}>
              Updated till - {formatDate(data.last_parsed_time)}
            </p>
          )
        }
      >
        <ToggleButtonsTrasnferList
          config={tabConfig}
          {...{ tabIndex, onTabChange }}
        />
      </DashboardCardHeader>
      {isLoading ? (
        <Loading />
      ) : (
        data && (
          <div className={classes.container}>
            <div className={classes.widgetLineWrapper}>
              {headerRow.map((label) => (
                <div
                  className={classes.widget}
                  key={label}
                  style={{
                    background: "rgba(226, 19, 110, 0.7)",
                  }}
                >
                  <HeaderAndValue
                    value={label}
                    styles={{
                      valueClass: {
                        fontSize: 16,
                        marginTop: 0,
                        textAlign: "center",
                        display: "flex",
                        flexDirection: "column",
                        color: "white",
                        padding: "8px 0",
                      },
                    }}
                  />
                </div>
              ))}
            </div>
            {data.data
              .sort((a, b) => {
                if (a.year > b.year) {
                  return -1;
                } else if (a.year < b.year) {
                  return 1;
                }

                return b.month - a.month;
              })
              .map((d, _i) => {
                return (
                  <div key={_i} className={classes.widgetLineWrapper}>
                    {config(d).map((c, _idx) => {
                      return (
                        <div
                          className={classes.widget}
                          key={c.label}
                          style={{
                            background: _idx === 0 && "rgba(199, 199, 199, 1)",
                          }}
                        >
                          {tabConfig[tabIndex].label === "Eligible" ? (
                            <HeaderAndValue
                              {...c}
                              value={c.valueEligible}
                              subValue={c.subValueEligible}
                              styles={{
                                labelClass: {
                                  fontSize: 12,
                                },
                                valueClass: {
                                  fontSize: 16,
                                  marginTop: 0,
                                  textAlign: "center",
                                  display: "flex",
                                  flexDirection: "column",
                                  color:
                                    _idx === 0
                                      ? "white"
                                      : "rgba(54, 43, 115, 1)",
                                },
                                subValueClass: {
                                  fontWeight: 300,
                                  fontSize: 12,
                                },
                              }}
                            />
                          ) : (
                            <HeaderAndValue
                              {...c}
                              styles={{
                                labelClass: {
                                  fontSize: 12,
                                },
                                valueClass: {
                                  fontSize: 16,
                                  marginTop: 0,
                                  textAlign: "center",
                                  display: "flex",
                                  flexDirection: "column",
                                  color:
                                    _idx === 0
                                      ? "white"
                                      : "rgba(54, 43, 115, 1)",
                                },
                                subValueClass: {
                                  fontWeight: 300,
                                  fontSize: 12,
                                },
                              }}
                            />
                          )}
                        </div>
                      );
                    })}
                  </div>
                );
              })}
          </div>
        )
      )}
    </DashboardWidgetCard>
  );
}
